import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {PaginationModel} from "../../../../../../../shared/components/data-table/pagination.model";
import {PolicyFilterPipe} from "../../../../../../../shared/pipes/policy-filter/policy-filter.pipe";
import {DetailsTableComponent} from "../../details/details-table/details-table.component";
import {ResourceModel} from "../../../../../../../core/models/resources/resource.model";
import {Const, ResourceState, ResourceType} from 'src/app/core/helpers/Const';
import { Utils } from 'src/app/core/helpers/Utils';
import {NotificationsService} from "../../../../../../../service/services/misc/notifications/notifications.service";
import {DialogService} from "@fundamental-ngx/core/dialog";
import {IconLookup} from "@fortawesome/free-regular-svg-icons";
import {AppConfigService} from "../../../../../../../service/services/misc/app-config/app-config.service";
import { ResourceDataService } from 'src/app/service/services/rest/resource-data/resource-data.service';

import {
    ReadAccessGrantedDialogComponent
} from "../../../../../dialogs/read-access-granted-dialog/read-access-granted-dialog.component";
import {firstValueFrom} from "rxjs";
import {FetchResponse, PollingService} from "../../../../../../../service/services/misc/polling";
import {NavigationService} from "../../../../../../../service/services/misc/navigation/navigation.service";

@Component({
    selector: 'app-rtd-type-resources-table',
    templateUrl: './rtd-type-resources-table.component.html',
    styleUrls: ['./rtd-type-resources-table.component.scss'],
    standalone: false
})
export class RtdTypeResourcesTableComponent extends DetailsTableComponent {
    pagination = new PaginationModel();
    Utils = Utils;
  @Input() rtdResources: any;
  filterPipe = PolicyFilterPipe;
  @Input() resource: ResourceModel;
  @Input() loading = false;
  @Input() errorDetails!: string;

  @Input() hasPrivilegesPermissions!: boolean;
  @Input() rtdResourcesStateMessage = false;
  @Output() permissionsAdded: EventEmitter<boolean> = new EventEmitter<boolean>();
  MAX_POLLING_NO_STATUS = 5;
  ResourceType = ResourceType;
  // @ts-ignore
  readonly navigateToPathIcon: IconLookup = {prefix: 'workspace', iconName: 'inspect'};
  notReadyMessage = "This managed service hasn't reached the Ready state. As a result, some RTD type resources might not be listed. Please try again later.";

  accessPath = "Can't access a path?";
  showDetails: boolean = false; // Flag to toggle details



  NO_VALUE_LABEL = Const.NO_VALUE_LABEL;
  Const = Const;


  constructor(private notificationsService: NotificationsService, private _appConfigService: AppConfigService,
              private ref: ChangeDetectorRef, private resourceDataService: ResourceDataService, private dialogService: DialogService,
              private pollingService: PollingService, private navigationService: NavigationService
  ) {
      super();
  }
  onNavigateToPathPress(path: string) {
      const baseUrl = "/app#";

      const [org, ...pathList] = path.substring(1).split('/');
      const params = ["project", Const.ALL_PROJECTS_PARAM, "organizations", org, Const.RESOURCE_GROUPS];
      const navigationExtras = {
          "queryParams": {
              "path": "/" + pathList.join('/')
          }
      };

      const url = this.navigationService.getNavigationUrl(params, navigationExtras);
      window.open(baseUrl + url, "_blank");
  }

  toggleDetails(event: Event): void {
      event.preventDefault(); // Prevent default anchor behavior
      this.showDetails = !this.showDetails;
  }

  onCopyPath(path: string) {
      this.notificationsService.showMessageToast($localize`:XMSG|Toast for copying path@@general.copyPathToast:Path copied to your clipboard`);
      Utils.copyToClipboard(path);
  }

  async onSuccess(res: any) {
      try {
          this.pollResourceStatus();
      } catch (e) {
      }

  }
  onError(error: Error) {
      this.errorDetails = error?.message;
      this.loading = false;
      this.ref.detectChanges();
  }

  pollResourceStatus() {
      let fetchUrl = `/api/resources/${this.resource?.apiVersion}/${this.resource?.resource?.type}${this.resource?.resource?.metadata?.path}/${this.resource?.resource?.metadata?.name}`;
      //
      let noStatusCount = 0;
      this.pollingService.addUrlTask(fetchUrl, (response: FetchResponse) => {
          if (!response?.ok) {
              this.onError(response?.body);
              return true;
          }

          let resource = response.body;

          let readyCondition = resource.status?.conditions?.find((condition: any) => condition.type === "Ready");
          if (!readyCondition) {
              noStatusCount++;
          }

          const setNoStatus = noStatusCount > this.MAX_POLLING_NO_STATUS;

          if (setNoStatus) {
              // Reached max polling. Stop polling.
              this.rtdResourcesStateMessage = true;
              return true;
          } else if (!resource.status) {
              // Has no status. Continue polling.
              return false;
          }
          // if reached a final status, stop polling (return true). If not, continue polling (return false).
          if (!this.isResourceInProcess(this.getState(resource, false))) {
              const msg = 'Updating resource ' + resource?.metadata?.name + ' (' + resource?.type + ')...';
              this.notificationsService.showMessageToast(msg);
              this.permissionsAdded.emit(true);
              this.dialogService.open(ReadAccessGrantedDialogComponent, {
                  width: '500px',
              });

              return true;

          } else {
              return false;
          }

      });
  }

  isResourceInProcess(state: ResourceState) {
      return (
          !state ||
            state === ResourceState.IN_PROCESS ||
            state === ResourceState.TERMINATING
      );
  }

  isGenerationUpdated(resource: any, readyCondition: any) {
      return resource.metadata.generation === readyCondition.observedGeneration;
  }

  getState(resource: any, setUnknown: boolean) {
      // First check if there is a deletion time, which means the resource is in a deleting phase
      if (resource?.resource?.metadata?.deletionTime) {
          return ResourceState.TERMINATING;
      }

      const conditions = resource.status?.conditions;
      if (!conditions) {
          return setUnknown ? ResourceState.UNKNOWN : ResourceState.IN_PROCESS;
      }

      let readyCondition = conditions.find((condition: any) => condition.type === "Ready");
      if (!readyCondition) {
          return setUnknown ? ResourceState.UNKNOWN : ResourceState.IN_PROCESS;
      }

      const readyState  = Utils.mapReadyReasonToState(readyCondition);
      if (readyState) {
          return readyState;
      }

      if (!this.isGenerationUpdated(resource, readyCondition)) {
          return setUnknown ? ResourceState.UNKNOWN : ResourceState.IN_PROCESS;
      }

      if (readyCondition.status === "True") {
          return ResourceState.READY;
      } else {
          return readyCondition.reason === ResourceState.IN_PROCESS ? ResourceState.IN_PROCESS : ResourceState.NOT_READY;
      }
  }

  updateUserPrincipals() {
      let userExist;
      let userEmail = this._appConfigService.getUserInfoModel()?.email;
      let principals: [{ subject: { name: string, type: string } }];

      if (!this.resource?.resource?.spec?.authorization?.principals) {
          try {
              this.resource.resource.spec.authorization["principals"] =
                      [
                          {
                              "subject": {
                                  "name": userEmail,
                                  "type": "User"
                              }
                          },
                      ];

          } catch (e) {
          }

      } else {
          principals = this.resource?.resource?.spec?.authorization?.principals;
          userExist = principals?.find(principal => principal.subject.name === userEmail && principal.subject.type === 'User');
          if (!userExist) {
              principals.push({
                  subject: {
                      name: userEmail,
                      type: "User"
                  }
              });
          }
      }
  }

  async onShowResources() {
      this.loading = true;

      this.updateUserPrincipals();
      try {
          const resourceDataMethod = this.resourceDataService.update('', '', {parameters: this?.resource?.resource});

          const data = await firstValueFrom(resourceDataMethod);

          this.onSuccess(data);
      } catch (error: any) {
          this.onError(error);
      }

      this.ref.detectChanges();
  }
}
